import { motion } from "framer-motion";
import { Layers, Palette, Boxes, ShieldCheck, ArrowRight } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "../../lib/motion";

const FEATURES = [
  { icon: Palette, title: "White-Label", body: "Fully brandable to any organization — your colors, your identity." },
  { icon: Boxes, title: "Multi-Module", body: "Composable modules for courses, assessments, and certification paths." },
  { icon: Layers, title: "Scalable", body: "From a single cohort to enterprise-wide rollouts across regions." },
  { icon: ShieldCheck, title: "Tactical-Ready", body: "Built for complex, high-stakes, and offline-tolerant environments." },
];

export const TrainingHub = () => {
  return (
    <section id="training" className="section-pad relative bg-[#DCE4EE]" data-testid="training-section">
      <div className="hairline-light absolute left-0 right-0 top-0" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Platform intro */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 gap-12 lg:grid-cols-12"
        >
          <motion.div variants={fadeUp} className="lg:col-span-5">
            <p className="overline overline-dark mb-6">/ Platform</p>
            <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0C2340] md:text-6xl">
              Tide Water
              <span> Training Hub</span>
            </h2>
            <p className="mt-6 font-body text-lg leading-relaxed text-[#1E3A5F]">
              A white-label, multi-module learning management system for
              organizations. One platform, infinite deployments — engineered so
              every organization can train its people, their way.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-6 lg:col-start-7">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`training-feature-${f.title.toLowerCase().replace(/\s|-/g, "")}`}
                  className="card-light group rounded-sm p-6 transition-all duration-500 hover:border-[#0C2340]/25"
                >
                  <Icon size={26} strokeWidth={1.4} className="text-[#0C2340] transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="mt-5 font-display text-2xl text-[#0C2340]">{f.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-[#1E3A5F]">{f.body}</p>
                </div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Raven implementation */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="card-light mt-20 flex flex-col items-start justify-between gap-8 overflow-hidden rounded-sm p-8 md:flex-row md:items-center md:p-12"
          data-testid="raven-block"
        >
          <div className="max-w-xl">
            <p className="overline overline-dark mb-4">/ Current Implementation</p>
            <h3 className="font-display text-3xl text-[#0C2340] md:text-5xl">
              Raven Training Hub
            </h3>
            <p className="font-mono-2 mt-3 text-[0.7rem] uppercase tracking-[0.2em] text-[#2E4A68]">
              Powered by Tide Water Studios Inc.
            </p>
            <p className="mt-5 font-body text-base leading-relaxed text-[#1E3A5F]">
              A live deployment of our platform — proof that the Training Hub
              adapts cleanly to a distinct brand, curriculum, and operational
              context.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex h-24 w-24 items-center justify-center rounded-full border border-[#0C2340]/20 md:h-32 md:w-32">
              <span className="font-display text-4xl md:text-5xl">R</span>
            </div>
            <ArrowRight className="text-[#8CA3B8]" size={28} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
